import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'FOOD', label: 'Food & Beverage', icon: '🍽️' },
  { value: 'TRANSPORT', label: 'Transportation', icon: '🚗' },
  { value: 'ACTIVITY', label: 'Activities', icon: '🎯' },
  { value: 'SERVICE', label: 'Services', icon: '🛎️' },
  { value: 'OTHER', label: 'Other', icon: '📦' },
];

const AddOns = () => {
  const { selectedProperty } = useOutletContext();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    per_night: false,
    category: 'OTHER',
    available: true
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchAddons();
    }
  }, [selectedProperty]);

  const fetchAddons = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/addons/property/${selectedProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddons(response.data);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAddOn = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/addons/property/${selectedProperty.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Add-on created successfully');
      setDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        price: 0,
        per_night: false,
        category: 'OTHER',
        available: true
      });
      fetchAddons();
    } catch (error) {
      toast.error('Failed to create add-on');
    }
  };

  const handleToggleAvailability = async (addonId, currentAvailability) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/addons/${addonId}?available=${!currentAvailability}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Availability updated');
      fetchAddons();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (addonId) => {
    if (!confirm('Delete this add-on?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/addons/${addonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Add-on deleted');
      fetchAddons();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getCategoryInfo = (category) => {
    return categories.find(c => c.value === category) || categories[categories.length - 1];
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="addons-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Add-ons & Upsells</h1>
          <p className="text-slate-600 mt-1">Manage extra services and amenities</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-addon-button">
              <Plus size={18} />
              New Add-on
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Add-on</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAddOn} className="space-y-4" data-testid="addon-form">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Airport Pickup"
                  required
                  data-testid="addon-name-input"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Round-trip airport transfer"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                    required
                    data-testid="addon-price-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="per-night"
                  checked={formData.per_night}
                  onChange={(e) => setFormData({...formData, per_night: e.target.checked})}
                />
                <Label htmlFor="per-night" className="cursor-pointer">Charge per night</Label>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-addon-button">
                Create Add-on
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {addons.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No add-ons yet. Create services guests can book.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" />
              Create First Add-on
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="addons-list">
          {addons.map((addon) => {
            const categoryInfo = getCategoryInfo(addon.category);
            return (
              <Card key={addon.id} className="hover:shadow-lg transition-shadow" data-testid={`addon-${addon.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{categoryInfo.icon}</span>
                      <span>{addon.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(addon.id)}
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600">{addon.description}</p>
                  
                  <div>
                    <p className="text-2xl font-bold text-cyan-600">
                      ${addon.price}
                      {addon.per_night && <span className="text-sm text-slate-500">/night</span>}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t">
                    <Label className="text-sm">Available</Label>
                    <Switch
                      checked={addon.available}
                      onCheckedChange={() => handleToggleAvailability(addon.id, addon.available)}
                      data-testid={`toggle-${addon.id}`}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddOns;
